import {
	RiArrowLeftDoubleLine,
	RiArrowLeftSLine,
	RiArrowRightDoubleLine,
	RiArrowRightSLine,
} from '@remixicon/react'
import { type Table } from '@tanstack/react-table'
import { Button } from '#app/components/ui/button.tsx'

import { cn } from '#app/utils/misc.tsx'

interface Props<TData> {
	table: Table<TData>
	pageSize: number
	hideSelectedSummary?: boolean
}

export function DataTablePagination<TData>({
	table,
	pageSize,
	hideSelectedSummary,
}: Props<TData>) {
	const paginationButtons = [
		{
			icon: RiArrowLeftDoubleLine,
			onClick: () => table.setPageIndex(0),
			disabled: !table.getCanPreviousPage(),
			srText: 'First page',
			mobileView: 'hidden sm:block',
		},
		{
			icon: RiArrowLeftSLine,
			onClick: () => table.previousPage(),
			disabled: !table.getCanPreviousPage(),
			srText: 'Previous page',
			mobileView: '',
		},
		{
			icon: RiArrowRightSLine,
			onClick: () => table.nextPage(),
			disabled: !table.getCanNextPage(),
			srText: 'Next page',
			mobileView: '',
		},
		{
			icon: RiArrowRightDoubleLine,
			onClick: () => table.setPageIndex(table.getPageCount() - 1),
			disabled: !table.getCanNextPage(),
			srText: 'Last page',
			mobileView: 'hidden sm:block',
		},
	]

	const totalRows = table.options.manualFiltering
		? table.getCoreRowModel().rows.length
		: table.getFilteredRowModel().rows.length
	const currentPage = table.getState().pagination.pageIndex
	const hasRows = totalRows > 0
	const firstRowIndex = hasRows ? currentPage * pageSize + 1 : 0
	const lastRowIndex = hasRows
		? Math.min(totalRows, firstRowIndex + pageSize - 1)
		: 0

	return (
		<div className="flex items-center justify-between">
			{hideSelectedSummary ? (
				<div />
			) : (
				<div className="text-sm text-gray-500 tabular-nums">
					{table.getFilteredSelectedRowModel().rows.length} of {totalRows}{' '}
					row(s) selected.
				</div>
			)}
			<div className="flex items-center gap-x-6 lg:gap-x-8">
				<p className="hidden text-sm text-gray-500 tabular-nums sm:block">
					Showing{' '}
					<span className="font-medium text-gray-900 dark:text-gray-50">
						{firstRowIndex}-{lastRowIndex}
					</span>{' '}
					of{' '}
					<span className="font-medium text-gray-900 dark:text-gray-50">
						{totalRows}
					</span>
				</p>
				<div className="flex items-center gap-x-1.5">
					{paginationButtons.map((button, index) => (
						<Button
							key={index}
							variant="outline"
							className={cn(button.mobileView, 'p-1.5')}
							onClick={() => {
								button.onClick()
								table.resetRowSelection()
							}}
							disabled={button.disabled}
						>
							<span className="sr-only">{button.srText}</span>
							<button.icon className="size-4 shrink-0" aria-hidden="true" />
						</Button>
					))}
				</div>
			</div>
		</div>
	)
}
